import { Component, Element, Event, EventEmitter, Host, Method, Prop, State, h } from '@stencil/core';
import { Alignment } from '../types';
import {
  CheckableFormCompoment,
  HiddenFormElementSlot,
  IndeterminateFormCompoment,
  Validator,
  connectForm,
  defaultValidator,
  disconnectForm,
  getLabelText,
  hasText,
  initializeValidators,
} from '../../utils';

/** @slot - Adds label text. */
@Component({
  shadow: true,
  styleUrl: 'checkbox.css',
  tag: 'abds-checkbox',
})
export class Checkbox implements CheckableFormCompoment, IndeterminateFormCompoment {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsCheckboxElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /** Sets name attribute. */
  @Prop({ reflect: true }) name!: string;

  /**  Aligns input and label. */
  @Prop({ reflect: true }) alignment: Alignment = 'start';

  /** Sets checked state. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) checked = false;

  /** Disables checkbox interaction.  */
  @Prop({ reflect: true }) disabled = false;

  /** Sets error state. */
  @Prop({ reflect: true }) error = false;

  /** Sets indeterminate state. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) indeterminate = false;

  /** Sets required state. */
  @Prop({ reflect: true }) required = false;

  /** Sets custom error message for required state. */
  @Prop() requiredMessage: string;

  /** Sets tooltip text in embedded abds-label. */
  @Prop({ reflect: true }) tooltip: string;

  /**
   * Form submission value if selected. Default for checked checkbox is **on**:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#attr-value
   */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) value = 'on';

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  /** initial render status */
  private isInitialRender = true;

  /** instance validation */
  private validator: Validator<any> = defaultValidator();

  /** toggle element */
  private toggleEl: HTMLDivElement;

  /** text to be used for label */
  private labelText: string;

  /** hidden input element */
  hiddenElement: HTMLInputElement;

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private toggle = (): void => {
    this.checked = !this.checked;
    this.indeterminate = false;
    this.setFocus();
    this.abdsChange.emit();
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === ' ') {
      event.preventDefault();

      if (!event.repeat) this.toggle();
    }
  };

  //--------------------------------------------------------------------------
  //
  //  Private State
  //
  //--------------------------------------------------------------------------

  /** icon to be used by abds-icon */
  @State() private icon = '';

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  /**
   * Sets focus on native `input` in `abds-checkbox`.
   * @internal
   */
  @Method()
  async setFocus(): Promise<void> {
    this.toggleEl?.focus();
  }

  //--------------------------------------------------------------------------
  //
  //  Events
  //
  //--------------------------------------------------------------------------

  /** Fired when the element's value is changed. */
  @Event() abdsChange: EventEmitter;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentWillLoad() {
    this.validator = initializeValidators(this);
  }

  componentWillRender() {
    if (this.checked) this.icon = 'check-1';
    if (this.indeterminate) this.icon = 'subtract';
    if (!this.checked && !this.indeterminate) this.icon = '';
  }

  componentDidRender() {
    if (this.isInitialRender) this.isInitialRender = false;
  }

  connectedCallback() {
    this.labelText = getLabelText(this.host);

    connectForm(this);
  }

  disconnectedCallback() {
    disconnectForm(this);
  }

  render() {
    const isInvalid = !this.isInitialRender && this.required && !this.checked;

    return (
      <Host onClick={this.disabled ? null : this.toggle} onKeyDown={this.disabled ? null : this.onKeyDown}>
        <div class="checkbox-label-container">
          <HiddenFormElementSlot component={this} type="checkbox" />
          <div
            aria-checked={this.checked.toString()}
            aria-label={this.labelText || null}
            class={{ 'checkbox-icon-container': true, invalid: isInvalid }}
            ref={(toggleEl) => (this.toggleEl = toggleEl)}
            role="checkbox"
            tabIndex={this.disabled ? undefined : 0}
          >
            <abds-icon icon={this.icon} size="md" stroke-width="bold"></abds-icon>
          </div>
          {hasText(this.host) && (
            <abds-label required={this.required} tooltip={this.tooltip} weight="medium">
              <slot />
            </abds-label>
          )}
        </div>
        {(!this.isInitialRender || this.error) &&
          hasText(this.host) &&
          !this.validator.validate(this.hiddenElement) && (
            <abds-supporting-text errorText={this.validator.errorMessage}></abds-supporting-text>
          )}
      </Host>
    );
  }
}
